import ks from "@shared/kalmstromShared4";
import { kPowerAutomatePackage } from "@shared/kPowerAutomatePackage";
import { IListDefinition, IFieldDefinition, IViewDefinition, IDataRow, IAggregation } from "../DBInterfaces";
import DBState from "../DBState";
import DBOutputSidebar from "../DBOutputSidebar";

// listTitle  — set on list-creation POST: response Id is stored in ListGUIDMap[listTitle]
// lookupList — set on lookup-field POST:  __GUID__ in body is replaced from ListGUIDMap[lookupList]
// ctName     — set on CT GET:             response value[0].Id.StringValue stored in CTGUIDMap[ctName]
// lookupCT   — set on CT-binding POST:    __CTID__ in body is replaced from CTGUIDMap[lookupCT]
interface IFlowRequest {
    comment?: string;
    method: string;
    uri: string;
    body: string;
    listTitle?: string;
    lookupList?: string;
    ctName?: string;
    lookupCT?: string;
}

export default class DBGeneratorPowerAutomate {

    // #region Public entry points

    public static ShowPowerAutomate = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const appCode: string = DBState.GetAppCode();
            const flowJson: string = DBGeneratorPowerAutomate.GenerateFlowDefinitionJSON();
            const filename: string = appCode + "-CreateLists-Flow.json";
            DBOutputSidebar.ShowOutputSidebar("Power Automate Flow", flowJson, filename, "application/json");
            DBOutputSidebar.ShowFlowExportButtons();
            DBOutputSidebar.ShowOutputNotes([
                { list: "How to import the ZIP", note: "Open make.powerapps.com", url: "https://make.powerapps.com/" },
                { list: "How to import the ZIP", note: "Go to Solutions \u2192 Import solution" },
                { list: "How to import the ZIP", note: "Upload the downloaded ZIP file" },
                { list: "After import", note: "Find the flow in Solutions \u2192 TCCreateSharePointLists" },
                { list: "After import", note: "Edit the flow and connect your SharePoint account" },
                { list: "After import", note: "Save and turn on the flow" },
                { list: "Note", note: "Do NOT use My Flows \u2192 Import \u2014 that is for the old package format" }
            ]);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerAutomate.ShowPowerAutomate");
        }
    };

    public static ExportFlowPackage = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const appCode: string = DBState.GetAppCode();
            const zipData: Uint8Array = DBGeneratorPowerAutomate.BuildFlowPackage(appCode);
            const filename: string = appCode + "-CreateLists-Flow.zip";
            kPowerAutomatePackage.Download(zipData, filename);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerAutomate.ExportFlowPackage");
        }
    };

    // #endregion

    // #region Solution package building

    private static BuildFlowPackage = (appCode: string): Uint8Array => {
        const flowName: string = appCode + " - Create SharePoint Lists";
        const solutionBaseName: string = appCode + "CreateSharePointLists";
        const flowDef: object = DBGeneratorPowerAutomate.BuildFlowDefinition();
        return kPowerAutomatePackage.Build(flowDef, flowName, solutionBaseName,
            [{ apiName: "shared_sharepointonline", displayName: "SharePoint Online" }],
            ks.kApplication.version);
    };

    // #endregion

    // #region Flow definition

    private static GenerateFlowDefinitionJSON = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            return JSON.stringify(DBGeneratorPowerAutomate.BuildFlowDefinition(), null, 2);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerAutomate.GenerateFlowDefinitionJSON");
            return "";
        }
    };

    // Single-connection architecture: ONE ApiConnection action lives inside the foreach loop.
    // The flow designer therefore shows exactly one "Connections" entry for the user to configure.
    //
    // Runtime GUID capture:
    //   ListGUIDMap (Object variable) — populated by Capture_List_GUID after each list-creation POST.
    //   CTGUIDMap   (Object variable) — populated by Capture_CT_GUID after each content-type GET.
    //
    // Placeholder replacement:
    //   Compose_Request_Body replaces __GUID__ and __CTID__ in the body string at foreach iteration
    //   time, using the already-populated map variables. If a body contains no placeholder the
    //   replace() is a no-op.
    private static BuildFlowDefinition = (): object => {
        const requests: IFlowRequest[] = DBGeneratorPowerAutomate.BuildAllRequests();
        // OpenApiConnection format (not ApiConnection) is required for solution-imported flows.
        // ApiConnection + apiId/connectionName/operationId host causes "Connector not found" in
        // the flow editor. OpenApiConnection uses inputs.parameters (parameters/method, parameters/uri,
        // etc. with literal "/" in key names — SP connector convention).
        // connectionName must be the connector API name — the key used in properties.connectionReferences
        // in the flow wrapper. The Dataverse logical name ("new_sharedsharepointonline") is stored
        // inside connection.connectionReferenceLogicalName in the wrapper and in customizations.xml.
        const spHost: object = {
            apiId: "/providers/Microsoft.PowerApps/apis/shared_sharepointonline",
            connectionName: "shared_sharepointonline",
            operationId: "HttpRequest"
        };

        // Compose expression: replace __GUID__ then __CTID__ using map variables.
        // PA's ?[] accessor throws when the key is null (missing optional field). Guard with
        // coalesce(..., '__NO_KEY__'): a sentinel never added to either map, so it returns null
        // → outer coalesce produces '' → replace() is a no-op for requests without that flag.
        const composeExpr: string =
            "@replace(" +
                "replace(" +
                    "items('Process_All_Requests')?['body'], " +
                    "'__GUID__', " +
                    "coalesce(variables('ListGUIDMap')?[coalesce(items('Process_All_Requests')?['lookupList'], '__NO_KEY__')], '')" +
                "), " +
                "'__CTID__', " +
                "coalesce(variables('CTGUIDMap')?[coalesce(items('Process_All_Requests')?['lookupCT'], '__NO_KEY__')], '')" +
            ")";

        const actions: Record<string, object> = {
            "Initialize_ListGUIDMap": {
                "runAfter": {},
                "type": "InitializeVariable",
                "inputs": {
                    "variables": [{ "name": "ListGUIDMap", "type": "object", "value": {} }]
                }
            },
            "Initialize_CTGUIDMap": {
                "runAfter": { "Initialize_ListGUIDMap": ["Succeeded"] },
                "type": "InitializeVariable",
                "inputs": {
                    "variables": [{ "name": "CTGUIDMap", "type": "object", "value": {} }]
                }
            },
            "Initialize_Requests": {
                "runAfter": { "Initialize_CTGUIDMap": ["Succeeded"] },
                "type": "InitializeVariable",
                "inputs": {
                    "variables": [{ "name": "Requests", "type": "array", "value": requests }]
                }
            },
            "Process_All_Requests": {
                "runAfter": { "Initialize_Requests": ["Succeeded"] },
                "type": "Foreach",
                "foreach": "@variables('Requests')",
                "operationOptions": "Sequential",
                "actions": {
                    // Step 1: replace GUID/CTID placeholders in the body before sending.
                    "Compose_Request_Body": {
                        "runAfter": {},
                        "type": "Compose",
                        "inputs": composeExpr
                    },
                    // Step 2: the ONE SharePoint connector action — one "Connections" entry in designer.
                    // OpenApiConnection type with parameters/ keys (SP connector convention).
                    "Send_an_HTTP_request_to_SharePoint": {
                        "runAfter": { "Compose_Request_Body": ["Succeeded"] },
                        "type": "OpenApiConnection",
                        "inputs": {
                            "host": spHost,
                            "parameters": {
                                "dataset": "@triggerBody()?['text']",
                                "parameters/method": "@{items('Process_All_Requests')?['method']}",
                                "parameters/uri": "@{items('Process_All_Requests')?['uri']}",
                                "parameters/headers": {
                                    "accept": "application/json;odata=nometadata",
                                    "content-type": "application/json;odata=nometadata",
                                    "If-Match": "*"
                                },
                                "parameters/body": "@{outputs('Compose_Request_Body')}"
                            },
                            "authentication": "@parameters('$authentication')"
                        }
                    },
                    // Step 3a: if listTitle is set, store the new list GUID in ListGUIDMap.
                    // The response body's Id field is the list GUID (plain UUID, no braces).
                    // PA does not allow reading a variable inside the SetVariable that writes it
                    // ("self reference"). Workaround: compute the new value in a Compose first.
                    // Empty else branches are omitted — PA treats missing "else" as no-op.
                    "Capture_List_GUID": {
                        "runAfter": { "Send_an_HTTP_request_to_SharePoint": ["Succeeded"] },
                        "type": "If",
                        "expression": "@not(equals(coalesce(items('Process_All_Requests')?['listTitle'], ''), ''))",
                        "actions": {
                            "Compose_New_ListGUIDMap": {
                                "runAfter": {},
                                "type": "Compose",
                                "inputs": "@addProperty(variables('ListGUIDMap'), items('Process_All_Requests')?['listTitle'], body('Send_an_HTTP_request_to_SharePoint')?['Id'])"
                            },
                            "Set_ListGUIDMap": {
                                "runAfter": { "Compose_New_ListGUIDMap": ["Succeeded"] },
                                "type": "SetVariable",
                                "inputs": {
                                    "name": "ListGUIDMap",
                                    "value": "@outputs('Compose_New_ListGUIDMap')"
                                }
                            }
                        }
                    },
                    // Step 3b: if ctName is set, store the new content type Id in CTGUIDMap.
                    // CT creation POST returns the CT object directly (not a value[] array).
                    "Capture_CT_GUID": {
                        "runAfter": { "Capture_List_GUID": ["Succeeded"] },
                        "type": "If",
                        "expression": "@not(equals(coalesce(items('Process_All_Requests')?['ctName'], ''), ''))",
                        "actions": {
                            "Compose_New_CTGUIDMap": {
                                "runAfter": {},
                                "type": "Compose",
                                // CT creation POST returns the new CT object directly (no value wrapper).
                                // With odata=nometadata, Id may be { StringValue:"0x..." } or a plain string.
                                // Try StringValue first; fall back to the Id value itself.
                                "inputs": "@addProperty(variables('CTGUIDMap'), items('Process_All_Requests')?['ctName'], coalesce(body('Send_an_HTTP_request_to_SharePoint')?['Id']?['StringValue'], string(body('Send_an_HTTP_request_to_SharePoint')?['Id'])))"
                            },
                            "Set_CTGUIDMap": {
                                "runAfter": { "Compose_New_CTGUIDMap": ["Succeeded"] },
                                "type": "SetVariable",
                                "inputs": {
                                    "name": "CTGUIDMap",
                                    "value": "@outputs('Compose_New_CTGUIDMap')"
                                }
                            }
                        }
                    }
                }
            }
        };

        return {
            "$schema": "https://schema.management.azure.com/providers/Microsoft.Logic/schemas/2016-06-01/workflowdefinition.json#",
            "contentVersion": "1.0.0.0",
            "parameters": {
                "$connections": { "defaultValue": {}, "type": "Object" },
                "$authentication": { "defaultValue": {}, "type": "SecureObject" }
            },
            "triggers": {
                "manual": {
                    "type": "Request",
                    "kind": "Button",
                    "inputs": {
                        "schema": {
                            "type": "object",
                            "properties": {
                                "text": {
                                    "title": "SharePoint Site URL",
                                    "description": "e.g. https://contoso.sharepoint.com/sites/MySite",
                                    "type": "string"
                                }
                            }
                        }
                    }
                }
            },
            "actions": actions
        };
    };

    // #endregion

    // #region Request builders

    // All requests in execution order. The foreach loop processes them sequentially, capturing
    // GUIDs into ListGUIDMap and CTGUIDMap so later requests can resolve __GUID__/__CTID__.
    private static BuildAllRequests = (): IFlowRequest[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const requests: IFlowRequest[] = [];

            // Phase 0: Content type CREATEs — create each CT at site level and populate CTGUIDMap.
            // Deduped: one POST per unique CT name across all lists.
            const seenCTs: Set<string> = new Set<string>();
            DBState.Lists.forEach((list: IListDefinition): void => {
                if (!list.UseContentType || !list.ContentTypeName) { return; }
                if (seenCTs.has(list.ContentTypeName)) { return; }
                seenCTs.add(list.ContentTypeName);
                requests.push(DBGeneratorPowerAutomate.BuildContentTypeCreateRequest(
                    list.ContentTypeName, list.ContentTypeGroup));
            });

            // Phase 1: List creations (no settings yet — settings applied after data insertion).
            // Each list-creation request carries listTitle so Capture_List_GUID stores the GUID.
            DBState.Lists.forEach((list: IListDefinition): void => {
                requests.push(DBGeneratorPowerAutomate.BuildListCreationRequest(list));
            });

            // Phase 2: Non-lookup fields + their optional settings PATCHes.
            DBState.Lists.forEach((list: IListDefinition): void => {
                Object.keys(list.Properties).forEach((fieldName: string): void => {
                    const field: IFieldDefinition = list.Properties[fieldName];
                    if (field.Type !== "lookup") {
                        requests.push(DBGeneratorPowerAutomate.BuildFieldCreationRequest(
                            list.Title, fieldName, field));
                        DBGeneratorPowerAutomate.BuildFieldPatchRequests(list.Title, fieldName, field)
                            .forEach((req: IFlowRequest): void => { requests.push(req); });
                    }
                });
            });

            // Phase 3: Lookup fields — __GUID__ in body replaced at runtime from ListGUIDMap.
            // All list-creation requests have already been processed by this point in the loop,
            // so ListGUIDMap contains every list's GUID when these requests execute.
            DBState.Lists.forEach((list: IListDefinition): void => {
                Object.keys(list.Properties).forEach((fieldName: string): void => {
                    const field: IFieldDefinition = list.Properties[fieldName];
                    if (field.Type === "lookup" && field.LookupList) {
                        requests.push(DBGeneratorPowerAutomate.BuildLookupFieldRequest(
                            list.Title, fieldName, field));
                        DBGeneratorPowerAutomate.BuildFieldPatchRequests(list.Title, fieldName, field)
                            .forEach((req: IFlowRequest): void => { requests.push(req); });
                    }
                });
            });

            // Phase 4: Data insertion — before settings/CT binding so SP doesn't validate
            // items against a bound content type that lacks the list-level field definitions.
            DBState.Lists.forEach((list: IListDefinition): void => {
                DBGeneratorPowerAutomate.BuildDataInsertionRequests(list)
                    .forEach((req: IFlowRequest): void => { requests.push(req); });
            });

            // Phase 5: List settings (ContentTypesEnabled, EnableVersioning, etc.).
            DBState.Lists.forEach((list: IListDefinition): void => {
                const settingsReq: IFlowRequest | null =
                    DBGeneratorPowerAutomate.BuildListSettingsPatchRequest(list);
                if (settingsReq !== null) { requests.push(settingsReq); }
            });

            // Phase 6: Content type binding — __CTID__ replaced at runtime from CTGUIDMap.
            DBState.Lists.forEach((list: IListDefinition): void => {
                if (!list.UseContentType || !list.ContentTypeName) { return; }
                requests.push(DBGeneratorPowerAutomate.BuildContentTypeBindingRequest(
                    list.Title, list.ContentTypeName));
            });

            // Phase 7: Views (create, makedefault, add fields, aggregations, formatter).
            DBState.Lists.forEach((list: IListDefinition): void => {
                if (!list.Views || list.Views.length === 0) { return; }
                list.Views.forEach((view: IViewDefinition): void => {
                    DBGeneratorPowerAutomate.BuildViewRequests(list.Title, view)
                        .forEach((req: IFlowRequest): void => { requests.push(req); });
                });
            });

            return requests;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerAutomate.BuildAllRequests");
            return [];
        }
    };

    // List creation POST. listTitle marks the request so Capture_List_GUID stores the GUID.
    private static BuildListCreationRequest = (list: IListDefinition): IFlowRequest => {
        return {
            comment: `Create list: ${list.Title}`,
            method: "POST",
            uri: "_api/web/lists",
            body: JSON.stringify({
                "Title": list.Title,
                "BaseTemplate": list.ListTemplate || 100,
                "AllowContentTypes": true
            }),
            listTitle: list.Title
        };
    };

    // Content type CREATE (POST). Creates the CT at the site level; SP auto-generates a derived
    // ID (child of Item / 0x01). ctName marks the request so Capture_CT_GUID captures the new Id.
    // The POST response is the created CT object directly — no value[] wrapper.
    private static BuildContentTypeCreateRequest = (ctName: string, ctGroup: string | undefined): IFlowRequest => {
        const body: Record<string, string> = { "Name": ctName };
        if (ctGroup) { body["Group"] = ctGroup; }
        return {
            comment: `Create content type: ${ctName}`,
            method: "POST",
            uri: `_api/web/contenttypes`,
            body: JSON.stringify(body),
            ctName: ctName
        };
    };

    // Content type binding POST. lookupCT triggers __CTID__ replacement from CTGUIDMap at runtime.
    private static BuildContentTypeBindingRequest = (listTitle: string, ctName: string): IFlowRequest => {
        const escapedTitle: string = DBGeneratorPowerAutomate.EscapeODataString(listTitle);
        return {
            comment: `Bind content type '${ctName}' to list: ${listTitle}`,
            method: "POST",
            uri: `_api/web/lists/getbytitle('${escapedTitle}')/contenttypes/addavailablecontenttype`,
            body: '{"contentTypeId":"__CTID__"}',
            lookupCT: ctName
        };
    };

    // Lookup field creation POST. lookupList triggers __GUID__ replacement from ListGUIDMap at runtime.
    // The SchemaXml List attribute needs the GUID wrapped in braces: List="{guid}".
    // Wrapping is included here; the Compose step replaces __GUID__ with the bare GUID from the map.
    private static BuildLookupFieldRequest = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition
    ): IFlowRequest => {
        const escapedListTitle: string = DBGeneratorPowerAutomate.EscapeODataString(listTitle);
        // Use internalName as DisplayName — same reason as BuildFieldSchemaXml:
        // SP derives InternalName from DisplayName, not from the Name/StaticName attribute.
        const showField: string = DBGeneratorPowerAutomate.EscapeXmlAttr(field.LookupField || "Title");
        const required: string = field.Required === true ? ' Required=\\"TRUE\\"' : '';
        const mult: string = field.AllowMultipleValues === true ? ' Mult=\\"TRUE\\"' : '';
        const desc: string = field.Description
            ? ` Description=\\"${DBGeneratorPowerAutomate.EscapeXmlAttr(field.Description)}\\"`
            : '';
        // __GUID__ is the placeholder; Compose_Request_Body replaces it with the actual list GUID.
        const bodyStr: string =
            `{"parameters":{"SchemaXml":"<Field Type=\\"Lookup\\"` +
            ` DisplayName=\\"${internalName}\\" Name=\\"${internalName}\\"` +
            ` StaticName=\\"${internalName}\\" ShowField=\\"${showField}\\"${required}${mult}${desc}` +
            ` List=\\"{__GUID__}\\" />"}}`;
        return {
            comment: `Create lookup field: ${field.Title} (${internalName}) on ${listTitle}`,
            method: "POST",
            uri: `_api/web/lists/getbytitle('${escapedListTitle}')/fields/createfieldasxml`,
            body: bodyStr,
            lookupList: field.LookupList
        };
    };

    private static BuildFieldCreationRequest = (listTitle: string, internalName: string, field: IFieldDefinition): IFlowRequest => {
        const schemaXml: string = DBGeneratorPowerAutomate.BuildFieldSchemaXml(internalName, field);
        const body: object = { "parameters": { "SchemaXml": schemaXml } };
        const escapedTitle: string = DBGeneratorPowerAutomate.EscapeODataString(listTitle);
        return {
            comment: `Create field: ${field.Title} (${internalName}) on ${listTitle}`,
            method: "POST",
            uri: `_api/web/lists/getbytitle('${escapedTitle}')/fields/createfieldasxml`,
            body: JSON.stringify(body)
        };
    };

    private static BuildViewRequests = (listTitle: string, view: IViewDefinition): IFlowRequest[] => {
        const requests: IFlowRequest[] = [];
        const escapedTitle: string = DBGeneratorPowerAutomate.EscapeODataString(listTitle);
        const escapedViewTitle: string = DBGeneratorPowerAutomate.EscapeODataString(view.Title);

        const viewBody: Record<string, unknown> = {
            "Title": view.Title,
            "RowLimit": view.RowLimit || 30,
            "Paged": view.Paged !== false
        };
        if (view.ViewQuery) { viewBody["ViewQuery"] = view.ViewQuery; }
        if (view.Hidden === true) { viewBody["Hidden"] = true; }
        // ViewType: omit if empty or "html" (default); otherwise pass through
        if (view.ViewType && view.ViewType.toLowerCase() !== "html") {
            viewBody["ViewType"] = view.ViewType.toUpperCase();
        }
        // Scope: map string → integer (0=Default, 1=Recursive, 2=RecursiveAll, 3=FilesOnly)
        if (view.Scope) {
            const scopeMap: Record<string, number> = { "recursive": 1, "recursiveall": 2, "filesonly": 3 };
            const scopeVal: number | undefined = scopeMap[view.Scope.toLowerCase()];
            if (scopeVal !== undefined) { viewBody["Scope"] = scopeVal; }
        }

        requests.push({
            comment: `Create view: ${view.Title} on ${listTitle}`,
            method: "POST",
            uri: `_api/web/lists/getbytitle('${escapedTitle}')/views`,
            body: JSON.stringify(viewBody)
        });

        if (view.IsDefault === true) {
            requests.push({
                comment: `Set view '${view.Title}' as default on ${listTitle}`,
                method: "PATCH",
                uri: `_api/web/lists/getbytitle('${escapedTitle}')/views/getbytitle('${escapedViewTitle}')`,
                body: JSON.stringify({ "DefaultView": true })
            });
        }

        requests.push({
            comment: `Clear default view fields for: ${view.Title} on ${listTitle}`,
            method: "POST",
            uri: `_api/web/lists/getbytitle('${escapedTitle}')/views/getbytitle('${escapedViewTitle}')/viewfields/removeallviewfields`,
            body: "{}"
        });

        if (view.ViewFields && view.ViewFields.length > 0) {
            view.ViewFields.forEach((fieldName: string): void => {
                requests.push({
                    comment: `Add field '${fieldName}' to view '${view.Title}' on ${listTitle}`,
                    method: "POST",
                    uri: `_api/web/lists/getbytitle('${escapedTitle}')/views/getbytitle('${escapedViewTitle}')/viewfields/addviewfield('${fieldName}')`,
                    body: "{}"
                });
            });
        }

        // Aggregations: build XML string from IAggregation array and PATCH the view
        if (view.Aggregations && view.Aggregations.length > 0) {
            const aggregationsXml: string = view.Aggregations
                .map((a: IAggregation): string =>
                    `<FieldRef Name="${DBGeneratorPowerAutomate.EscapeXmlAttr(a.FieldName)}" Type="${DBGeneratorPowerAutomate.EscapeXmlAttr(a.Function)}"/>`)
                .join("");
            // SP requires Aggregations XML to be set before AggregationsStatus can be enabled.
            // A combined PATCH on a fresh view (empty Aggregations) returns 400 "Value does not
            // fall within the expected range." — split into two sequential requests.
            requests.push({
                comment: `Set aggregations XML for view '${view.Title}' on ${listTitle}`,
                method: "PATCH",
                uri: `_api/web/lists/getbytitle('${escapedTitle}')/views/getbytitle('${escapedViewTitle}')`,
                body: JSON.stringify({ "Aggregations": aggregationsXml })
            });
            requests.push({
                comment: `Enable aggregations for view '${view.Title}' on ${listTitle}`,
                method: "PATCH",
                uri: `_api/web/lists/getbytitle('${escapedTitle}')/views/getbytitle('${escapedViewTitle}')`,
                body: JSON.stringify({ "AggregationsStatus": "On" })
            });
        }

        // CustomFormatter: PATCH the view's JSON formatting definition
        if (view.CustomFormatter) {
            requests.push({
                comment: `Set custom formatter for view '${view.Title}' on ${listTitle}`,
                method: "PATCH",
                uri: `_api/web/lists/getbytitle('${escapedTitle}')/views/getbytitle('${escapedViewTitle}')`,
                body: JSON.stringify({ "CustomFormatter": view.CustomFormatter })
            });
        }

        return requests;
    };

    // Build PATCH requests for field-level settings that cannot be set via createfieldasxml.
    // Covers: ValidationFormula (+ MinLength LEN clause), ValidationMessage, CustomFormatter,
    // SelectionGroup (user fields), ShowInDisplayForm/EditForm/NewForm.
    private static BuildFieldPatchRequests = (listTitle: string, internalName: string, field: IFieldDefinition): IFlowRequest[] => {
        const patchBody: Record<string, unknown> = {};

        // Restore the user-friendly display name: field was created with DisplayName=internalName
        // so SP set InternalName=internalName correctly; now patch Title back to field.Title.
        if (field.Title && field.Title !== internalName) { patchBody["Title"] = field.Title; }

        // ValidationFormula: combine a MinLength LEN clause with any explicit formula.
        // MinLength > 0 generates =LEN([DisplayName])>=N, AND-ed with existing formula if present.
        let validationFormula: string = field.ValidationFormula || "";
        if (field.MinLength > 0) {
            const lenClause: string = `LEN([${field.Title}])>=${field.MinLength}`;
            if (validationFormula) {
                const formulaBody: string = validationFormula.startsWith("=")
                    ? validationFormula.substring(1)
                    : validationFormula;
                validationFormula = `=AND(${lenClause},${formulaBody})`;
            } else {
                validationFormula = `=${lenClause}`;
            }
        }
        if (validationFormula) { patchBody["ValidationFormula"] = validationFormula; }

        // ValidationMessage: explicit message wins; auto-generate only for MinLength with no message.
        if (field.ValidationMessage) {
            patchBody["ValidationMessage"] = field.ValidationMessage;
        } else if (field.MinLength > 0) {
            patchBody["ValidationMessage"] = `Minimum ${field.MinLength} characters required.`;
        }

        if (field.CustomFormatter) { patchBody["CustomFormatter"] = field.CustomFormatter; }
        if (field.Type === "user" && field.SelectionGroup) { patchBody["SelectionGroup"] = field.SelectionGroup; }
        // Only emit when explicitly false — true is the SharePoint default and needs no PATCH.
        if (field.ShowInDisplayForm === false) { patchBody["ShowInDisplayForm"] = false; }
        if (field.ShowInEditForm === false)    { patchBody["ShowInEditForm"] = false; }
        if (field.ShowInNewForm === false)     { patchBody["ShowInNewForm"] = false; }

        if (Object.keys(patchBody).length === 0) { return []; }
        const escapedTitle: string = DBGeneratorPowerAutomate.EscapeODataString(listTitle);
        const escapedField: string = DBGeneratorPowerAutomate.EscapeODataString(internalName);
        return [{
            comment: `Update field settings: ${field.Title} (${internalName}) on ${listTitle}`,
            method: "PATCH",
            uri: `_api/web/lists/getbytitle('${escapedTitle}')/fields/getbyinternalnameortitle('${escapedField}')`,
            body: JSON.stringify(patchBody)
        }];
    };

    // Build a PATCH request to apply IListSettings to an already-created list.
    // Returns null when the list has no Settings configured.
    private static BuildListSettingsPatchRequest = (list: IListDefinition): IFlowRequest | null => {
        if (!list.Settings) { return null; }
        const s = list.Settings;
        const body: Record<string, unknown> = {
            "EnableVersioning":    s.EnableVersioning,
            "EnableMinorVersions": s.EnableMinorVersions,
            "EnableModeration":    s.EnableModeration,
            "OnQuickLaunch":       s.OnQuickLaunch,
            "EnableAttachments":   s.EnableAttachments,
            "EnableFolderCreation":s.EnableFolderCreation,
            "ContentTypesEnabled": s.ContentTypesEnabled,
            "DisableGridEditing":  s.DisableGridEditing,
            "ForceCheckout":       s.ForceCheckout,
            "Hidden":              s.Hidden
        };
        // Only include numeric settings if non-zero to avoid invalid values
        if (s.MajorVersionLimit > 0) { body["MajorVersionLimit"] = s.MajorVersionLimit; }
        if (s.ReadSecurity > 0)      { body["ReadSecurity"] = s.ReadSecurity; }
        if (s.WriteSecurity > 0)     { body["WriteSecurity"] = s.WriteSecurity; }
        if (s.ListExperience > 0)    { body["ListExperienceOptions"] = s.ListExperience; }
        const escapedTitle: string = DBGeneratorPowerAutomate.EscapeODataString(list.Title);
        return {
            comment: `Apply settings for list: ${list.Title}`,
            method: "PATCH",
            uri: `_api/web/lists/getbytitle('${escapedTitle}')`,
            body: JSON.stringify(body)
        };
    };

    // Build POST requests to insert all data rows into a list.
    // Lookup field keys are automatically renamed to the required "FieldNameId" REST form.
    private static BuildDataInsertionRequests = (list: IListDefinition): IFlowRequest[] => {
        if (!list.Data || list.Data.length === 0) { return []; }
        const escapedTitle: string = DBGeneratorPowerAutomate.EscapeODataString(list.Title);
        // Identify lookup fields so their REST property name gets the "Id" suffix
        const lookupFields: Set<string> = new Set<string>(
            Object.keys(list.Properties).filter((fn: string): boolean => list.Properties[fn].Type === "lookup")
        );
        return list.Data.map((row: IDataRow, index: number): IFlowRequest => {
            const body: Record<string, unknown> = {};
            Object.keys(row).forEach((fieldName: string): void => {
                const value: string | number | boolean | null = row[fieldName];
                if (value === null || value === undefined) { return; }
                // Lookup values must be POSTed as "FieldNameId" (numeric item ID)
                const restKey: string = lookupFields.has(fieldName) ? fieldName + "Id" : fieldName;
                body[restKey] = value;
            });
            return {
                comment: `Insert data row ${index + 1} into ${list.Title}`,
                method: "POST",
                uri: `_api/web/lists/getbytitle('${escapedTitle}')/items`,
                body: JSON.stringify(body)
            };
        });
    };

    // #endregion

    // #region Field schema XML

    private static BuildFieldSchemaXml = (internalName: string, field: IFieldDefinition): string => {
        // RichText=true on a note field with no Format override is equivalent to Format="rich-text"
        const fieldType: string = field.Format ||
            (field.Type === "note" && field.RichText === true ? "rich-text" : field.Type);
        let xmlType: string = "Text";
        let extraAttrs: string = "";
        let innerXml: string = "";

        switch (fieldType) {
            case "note":
                xmlType = "Note";
                if (field.NumberOfLines) { extraAttrs += ` NumLines="${field.NumberOfLines}"`; }
                if (field.AppendOnly === true) { extraAttrs += ' AppendOnly="TRUE"'; }
                break;
            case "rich-text":
                xmlType = "Note";
                extraAttrs += ' RichText="TRUE" RichTextMode="FullHtml"';
                if (field.NumberOfLines) { extraAttrs += ` NumLines="${field.NumberOfLines}"`; }
                break;
            case "date":
                xmlType = "DateTime";
                extraAttrs += ' Format="DateOnly"';
                if (field.FriendlyDisplayFormat) { extraAttrs += ` FriendlyDisplayFormat="${DBGeneratorPowerAutomate.EscapeXmlAttr(field.FriendlyDisplayFormat)}"`; }
                break;
            case "date-time":
                xmlType = "DateTime";
                extraAttrs += ' Format="DateTime"';
                if (field.FriendlyDisplayFormat) { extraAttrs += ` FriendlyDisplayFormat="${DBGeneratorPowerAutomate.EscapeXmlAttr(field.FriendlyDisplayFormat)}"`; }
                break;
            case "currency":
                xmlType = "Currency";
                if (field.MinimumValue !== null && field.MinimumValue !== undefined) { extraAttrs += ` Min="${field.MinimumValue}"`; }
                if (field.MaximumValue !== null && field.MaximumValue !== undefined) { extraAttrs += ` Max="${field.MaximumValue}"`; }
                if (field.DisplayFormat && field.DisplayFormat !== "-1") { extraAttrs += ` Decimals="${field.DisplayFormat}"`; }
                break;
            case "url":
                xmlType = "URL";
                break;
            case "boolean":
                xmlType = "Boolean";
                break;
            case "integer":
                xmlType = "Integer";
                break;
            case "number":
                xmlType = "Number";
                if (field.MinimumValue !== null && field.MinimumValue !== undefined) { extraAttrs += ` Min="${field.MinimumValue}"`; }
                if (field.MaximumValue !== null && field.MaximumValue !== undefined) { extraAttrs += ` Max="${field.MaximumValue}"`; }
                if (field.ShowAsPercentage === true) { extraAttrs += ' Percentage="TRUE"'; }
                if (field.DisplayFormat && field.DisplayFormat !== "-1") { extraAttrs += ` Decimals="${field.DisplayFormat}"`; }
                break;
            case "user":
                xmlType = "User";
                if (field.AllowMultipleValues === true) { extraAttrs += ' Mult="TRUE"'; }
                if (field.SelectionMode) { extraAttrs += ` UserSelectionMode="${DBGeneratorPowerAutomate.EscapeXmlAttr(field.SelectionMode)}"`; }
                break;
            case "geolocation":
                xmlType = "Geolocation";
                break;
            case "string":
                if (field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) {
                    xmlType = "Choice";
                    if (field.FillInChoice === true) { extraAttrs += ' FillInChoice="TRUE"'; }
                    innerXml = "<CHOICES>";
                    field.Enum.forEach((c: string): void => { innerXml += `<CHOICE>${DBGeneratorPowerAutomate.EscapeXml(c)}</CHOICE>`; });
                    innerXml += "</CHOICES>";
                } else {
                    xmlType = "Text";
                    if (field.MaxLength) { extraAttrs += ` MaxLength="${field.MaxLength}"`; }
                }
                break;
            case "array":
                if (field.Items !== null && field.Items !== undefined
                    && field.Items.Enum !== null && field.Items.Enum !== undefined && field.Items.Enum.length > 0) {
                    xmlType = "MultiChoice";
                    if (field.FillInChoice === true) { extraAttrs += ' FillInChoice="TRUE"'; }
                    innerXml = "<CHOICES>";
                    field.Items.Enum.forEach((c: string): void => { innerXml += `<CHOICE>${DBGeneratorPowerAutomate.EscapeXml(c)}</CHOICE>`; });
                    innerXml += "</CHOICES>";
                } else {
                    xmlType = "Note";
                    if (field.NumberOfLines) { extraAttrs += ` NumLines="${field.NumberOfLines}"`; }
                }
                break;
            default:
                xmlType = "Text";
        }

        let xml: string = `<Field Type="${xmlType}"`;
        // Use internalName as DisplayName so SP derives InternalName = internalName.
        // (SP ignores the Name/StaticName attributes when determining InternalName and instead
        // derives it from the DisplayName. We patch the user-friendly Title separately below.)
        xml += ` DisplayName="${internalName}"`;
        xml += ` Name="${internalName}"`;
        xml += ` StaticName="${internalName}"`;
        if (field.Required === true) { xml += ' Required="TRUE"'; }
        if (field.EnforceUniqueValues === true) { xml += ' EnforceUniqueValues="TRUE" Indexed="TRUE"'; }
        if (field.Description) { xml += ` Description="${DBGeneratorPowerAutomate.EscapeXmlAttr(field.Description)}"`; }
        xml += extraAttrs;

        // Prepend <Default> before any type-specific child elements (e.g. <CHOICES>)
        if (field.DefaultValue !== null && field.DefaultValue !== undefined && field.DefaultValue !== "") {
            innerXml = `<Default>${DBGeneratorPowerAutomate.EscapeXml(field.DefaultValue)}</Default>` + innerXml;
        }

        if (innerXml) {
            xml += `>${innerXml}</Field>`;
        } else {
            xml += " />";
        }

        return xml;
    };

    // #endregion

    // #region String helpers

    private static EscapeODataString = (s: string): string => {
        return s.replace(/'/g, "''");
    };

    private static EscapeXml = (s: string): string => {
        return s
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
    };

    private static EscapeXmlAttr = (s: string): string => {
        return s
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
    };

    // #endregion
}
